import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import {
  getPublicInvoice,
  publicInvoicePdfUrl,
  type PublicInvoiceResponse,
} from "@/services/publicInvoice"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { serverDetail } from "@/services/public-fetch"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { FileText, Download, AlertTriangle } from "lucide-react"
import { formatQuantity } from "@/lib/money"
import { TenantClosedNotice } from "@/components/TenantClosedNotice"
import { isTenantClosedError } from "@/lib/tenant-closed"
import { formatIsoDateFr } from "@/lib/organisation-day"

function formatMoney(value: string | number, currency: string): string {
  const n = typeof value === "string" ? parseFloat(value) : value
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: currency || "EUR",
  }).format(n)
}

function formatDate(iso: string): string {
  return formatIsoDateFr(iso, { day: "2-digit", month: "long", year: "numeric" })
}

function statusBadge(code: string, label: string | null) {
  const map: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; text: string }> = {
    draft: { variant: "secondary", text: "Brouillon" },
    sent: { variant: "default", text: "Envoyée" },
    paid: { variant: "default", text: "Payée" },
    overdue: { variant: "destructive", text: "En retard" },
    cancelled: { variant: "outline", text: "Annulée" },
  }
  const def = map[code] ?? { variant: "outline" as const, text: label || code }
  return <Badge variant={def.variant} data-test="invoice-status-badge">{def.text}</Badge>
}

// Repli quand le serveur ne donne pas de raison.
const INVOICE_UNAVAILABLE =
  "Le lien que vous avez utilisé n'est plus valide ou la facture n'existe plus. Contactez votre interlocuteur pour obtenir un nouveau lien."

export default function PublicInvoicePage() {
  const { token } = useParams<{ token: string }>()

  const { data, isLoading, error } = useQuery({
    queryKey: ["public-invoice", token],
    queryFn: () => getPublicInvoice(token!),
    enabled: !!token,
    retry: false,
  })

  if (isLoading) {
    return (
      <PublicShell>
        <Card className="mx-auto max-w-2xl">
          <CardHeader>
            <CardTitle>Chargement de la facture…</CardTitle>
          </CardHeader>
        </Card>
      </PublicShell>
    )
  }

  if (isTenantClosedError(error)) {
    return (
      <PublicShell>
        <TenantClosedNotice />
      </PublicShell>
    )
  }

  if (error || !data) {
    return (
      <PublicShell>
        <Card className="mx-auto max-w-2xl" data-test="invoice-error-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Facture indisponible
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p>{pipe(serverDetail(error), O.getOrElse(() => INVOICE_UNAVAILABLE))}</p>
          </CardContent>
        </Card>
      </PublicShell>
    )
  }

  const invoice: PublicInvoiceResponse = data

  return (
    <PublicShell logoUrl={invoice.tenant.logo_url} tenantName={invoice.tenant.name}>
      <Card className="mx-auto max-w-3xl" data-test="public-invoice-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle data-test="invoice-number">
                Facture {invoice.invoice_number}
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Émise le {formatDate(invoice.issue_date)} · Échéance le {formatDate(invoice.due_date)}
              </p>
            </div>
            {statusBadge(invoice.status_code, invoice.status_label)}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <section data-test="invoice-client">
            <h2 className="mb-1 text-sm font-semibold text-muted-foreground">Destinataire</h2>
            <p className="font-medium">{invoice.client.account_name}</p>
            {invoice.client.contact_name && <p>{invoice.client.contact_name}</p>}
            {invoice.client.billing_address && <p>{invoice.client.billing_address}</p>}
            {(invoice.client.billing_postal_code || invoice.client.billing_city) && (
              <p>
                {invoice.client.billing_postal_code} {invoice.client.billing_city}
              </p>
            )}
            {invoice.client.contact_email && (
              <p className="text-sm text-muted-foreground">{invoice.client.contact_email}</p>
            )}
          </section>

          <section data-test="invoice-lines">
            <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Détail</h2>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-2 text-left">Description</th>
                  <th className="py-2 text-right">Quantité</th>
                  <th className="py-2 text-right">Prix unitaire</th>
                  <th className="py-2 text-right">Total HT</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map((line, idx) => (
                  <tr key={idx} className="border-b" data-test="invoice-line">
                    <td className="py-2">{line.description}</td>
                    <td className="py-2 text-right">{formatQuantity(line.quantity)}</td>
                    <td className="py-2 text-right">{formatMoney(line.unit_price, invoice.currency)}</td>
                    <td className="py-2 text-right">{formatMoney(line.total_amount, invoice.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section data-test="invoice-totals" className="space-y-1 border-t pt-4">
            <div className="flex justify-between text-sm">
              <span>Sous-total HT</span>
              <span>{formatMoney(invoice.subtotal_amount, invoice.currency)}</span>
            </div>
            {parseFloat(String(invoice.discount_amount)) > 0 && (
              <div className="flex justify-between text-sm">
                <span>Remise</span>
                <span>− {formatMoney(invoice.discount_amount, invoice.currency)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span>TVA</span>
              <span>{formatMoney(invoice.tax_amount, invoice.currency)}</span>
            </div>
            <div className="flex justify-between border-t pt-2 text-base font-semibold">
              <span>Total TTC</span>
              <span data-test="invoice-total">{formatMoney(invoice.total_amount, invoice.currency)}</span>
            </div>
          </section>

          {invoice.notes && (
            <section data-test="invoice-notes" className="rounded border bg-muted/30 p-3 text-sm">
              <p className="whitespace-pre-wrap">{invoice.notes}</p>
            </section>
          )}

          <div className="flex gap-2 pt-2">
            <Button asChild data-test="invoice-download-pdf">
              <a href={publicInvoicePdfUrl(token!)} target="_blank" rel="noopener noreferrer">
                <Download className="mr-2 h-4 w-4" />
                Télécharger la facture (PDF)
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </PublicShell>
  )
}

function PublicShell({
  children,
  logoUrl,
  tenantName,
}: {
  children: React.ReactNode
  logoUrl?: string | null
  tenantName?: string
}) {
  return (
    <div className="min-h-screen bg-muted/30 py-8" data-test="public-invoice-page">
      <header className="mb-6 flex items-center justify-center gap-3">
        {logoUrl ? (
          <img src={logoUrl} alt={tenantName ?? "Organisme"} className="h-10" />
        ) : (
          <FileText className="h-8 w-8 text-primary" />
        )}
        {tenantName && <span className="text-lg font-medium">{tenantName}</span>}
      </header>
      <main>{children}</main>
    </div>
  )
}
